import { useMemo, useState } from 'react';
import classNames from './ChangeLevelPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import StudentsTable, { convertStudentReportItemToStudentsTableData } from './StudentsTable';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import Button from '@/components/school-management/Button/Button';
import { BULK_LEVEL_CHANGE_LEVEL_IDS } from '@/school-management/utils/bulkLevelChange';
import DOMPurify from 'dompurify';

interface ChooseLevelProps {
  students: StudentReportItem[];
  hasNonActivedStudents?: boolean;
  onClose: () => void;
  onLevelSelected: (level: Option) => void;
}

const ChooseLevel = ({
  students,
  hasNonActivedStudents,
  onClose,
  onLevelSelected,
}: ChooseLevelProps) => {
  const { t } = useAdminTranslation();
  const [selectedLevel, setSelectedLevel] = useState<Option>();

  const studentsTableData = useMemo(() => {
    return (students || []).map(s => convertStudentReportItemToStudentsTableData(s, t));
  }, [students, t]);

  const levelOptions = useMemo(() => {
    return BULK_LEVEL_CHANGE_LEVEL_IDS.map(
      id =>
        ({
          label: t(`levelName_${id}`),
          value: String(id),
        }) as Option
    );
  }, [t]);

  return (
    <>
      <div>
        <div className={classNames.title}>{t('bulkActions.changeLevel.chooseLevel.title')}</div>
        <div
          className={classNames.description}
          style={{
            maxWidth: '700px',
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              t('bulkActions.changeLevel.chooseLevel.description', {
                count: students.length,
              }).toString()
            ),
          }}
        />
      </div>
      <StudentsTable students={studentsTableData} />
      {hasNonActivedStudents && (
        <div className={classNames.note}>
          {t('bulkActions.changeLevel.chooseLevel.hasNonActivedStudentsNote')}
        </div>
      )}
      <div>
        <div className={classNames.subTitle}>
          {t('bulkActions.changeLevel.chooseLevel.selectLevel')}
        </div>
        <div className={classNames.levelSelector}>
          <DropDown
            options={levelOptions}
            value={selectedLevel}
            onChange={setSelectedLevel}
            label={`${t('bulkActions.changeLevel.chooseLevel.level') || ''}:`}
            listClassName={classNames.levelList}
            autoClose={true}
          />
        </div>
      </div>
      <div className={classNames.footer}>
        <Button className={classNames.textButton} onClick={onClose}>
          {t('common.back')}
        </Button>
        <Button
          className={classNames.button}
          disabled={!selectedLevel}
          onClick={() => {
            if (selectedLevel) {
              onLevelSelected(selectedLevel);
            }
          }}
        >
          {t('bulkActions.changeLevel.chooseLevel.submitButton')}
        </Button>
      </div>
    </>
  );
};

export default ChooseLevel;
